package com.sweetlime.bingeamigo.presentation.features.auth

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Lock
import androidx.compose.foundation.text.ClickableText
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextFieldColors
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.sp
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import bingeamigo.composeapp.generated.resources.Res
import bingeamigo.composeapp.generated.resources.binge_amigo_header
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import org.jetbrains.compose.resources.painterResource
import com.sweetlime.bingeamigo.state.AuthState
import org.koin.compose.koinInject

@Composable
fun AuthContent(
    authViewModel: AuthViewModel = koinInject(),
    onAuthSuccess: () -> Unit,
    onPendingVerification: (email: String) -> Unit,
    authTextFieldColors: @Composable () -> TextFieldColors
) {

    var isLogin by remember { mutableStateOf(true) }
    var isForgotPassword by remember { mutableStateOf(false) }

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var emailError by remember { mutableStateOf<String?>(null) }
    var passwordError by remember { mutableStateOf<String?>(null) }

    val authState = authViewModel.state.collectAsState().value

    LaunchedEffect(authState) {
        when (authState) {
            is AuthState.Success -> onAuthSuccess()
            is AuthState.PendingVerification -> onPendingVerification(authState.email)
            else -> Unit
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF121212))
    ) {

        run {

        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

        Column(
            modifier = Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .padding(horizontal = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

            // Logo
            Image(
                painter = painterResource(Res.drawable.binge_amigo_header),
                contentDescription = "BingeAmigo",
                modifier = Modifier.height(80.dp)
            )

            Spacer(modifier = Modifier.height(16.dp))

            if (isForgotPassword) {
                // ── Forgot password flow ────────────────────────────────

                if (authState is AuthState.PasswordResetSent) {
                    // Success state
                    Text(
                        text = "Check your inbox!",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        color = BrandColors.TextPrimary
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    Text(
                        text = "We sent a reset link to ${authState.email}",
                        style = MaterialTheme.typography.bodyMedium,
                        color = BrandColors.TextSecondary,
                        textAlign = TextAlign.Center
                    )

                    Spacer(modifier = Modifier.height(36.dp))

                    Button(
                        onClick = {
                            isForgotPassword = false
                            authViewModel.resetState()
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        shape = RoundedCornerShape(16.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = BrandColors.Primary
                        )
                    ) {
                        Text(
                            text = "Back to Login",
                            color = Color.Black,
                            fontWeight = FontWeight.Bold
                        )
                    }
                } else {
                    // Forgot password form
                    Text(
                        text = buildAnnotatedString {
                            withStyle(SpanStyle(color = BrandColors.TextPrimary)) { append("Reset your ") }
                            withStyle(SpanStyle(color = BrandColors.Primary)) { append("password") }
                        },
                        style = MaterialTheme.typography.bodyMedium
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    Text(
                        text = "Enter your email and we'll send you a reset link.",
                        style = MaterialTheme.typography.bodySmall,
                        color = BrandColors.TextSecondary,
                        textAlign = TextAlign.Center
                    )

                    Spacer(modifier = Modifier.height(36.dp))

                    OutlinedTextField(
                        value = email,
                        onValueChange = {
                            email = it
                            emailError = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        placeholder = { Text("Email") },
                        leadingIcon = {
                            Icon(imageVector = Icons.Default.Email, contentDescription = null)
                        },
                        singleLine = true,
                        shape = RoundedCornerShape(16.dp),
                        colors = authTextFieldColors(),
                        isError = emailError != null,
                        supportingText = {
                            Text(emailError ?: "", color = BrandColors.Error, style = MaterialTheme.typography.labelSmall)
                        }
                    )

                    Spacer(modifier = Modifier.height(24.dp))

                    Button(
                        onClick = {
                            val trimmedEmail = email.trim()
                            if (trimmedEmail.isBlank()) {
                                emailError = "Email is required."
                                return@Button
                            }
                            if (!isValidEmail(trimmedEmail)) {
                                emailError = "Please enter a valid email address."
                                return@Button
                            }
                            authViewModel.sendPasswordReset(trimmedEmail)
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        shape = RoundedCornerShape(16.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = BrandColors.Primary
                        )
                    ) {
                        when (authState) {
                            is AuthState.Loading -> CircularProgressIndicator(
                                color = Color.Black,
                                strokeWidth = 2.dp,
                                modifier = Modifier.size(24.dp)
                            )
                            else -> Text(
                                text = "Send Reset Link",
                                color = Color.Black,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(20.dp))

                    if (authState is AuthState.Error) {
                        Text(
                            text = authState.message,
                            color = BrandColors.Error,
                            style = MaterialTheme.typography.bodySmall,
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(12.dp))
                    }

                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text("Remember your password?", color = Color.Gray)
                        TextButton(
                            onClick = {
                                isForgotPassword = false
                                emailError = null
                                authViewModel.resetState()
                            }
                        ) {
                            Text(
                                text = "Login",
                                color = BrandColors.Primary,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

            } else {
                // ── Login / Sign-up flow ────────────────────────────────

                Text(
                    text = buildAnnotatedString {
                        withStyle(SpanStyle(color = BrandColors.TextPrimary)) { append("Watch more. ") }
                        withStyle(SpanStyle(color = BrandColors.Primary)) { append("Share more. ") }
                        withStyle(SpanStyle(color = BrandColors.Secondary)) { append("Together.") }
                    },
                    style = MaterialTheme.typography.bodyMedium
                )

                Spacer(modifier = Modifier.height(36.dp))

                // Email
                OutlinedTextField(
                    value = email,
                    onValueChange = {
                        email = it
                        emailError = null
                    },
                    modifier = Modifier.fillMaxWidth(),
                    placeholder = { Text("Email") },
                    leadingIcon = {
                        Icon(imageVector = Icons.Default.Email, contentDescription = null)
                    },
                    singleLine = true,
                    shape = RoundedCornerShape(16.dp),
                    colors = authTextFieldColors(),
                    isError = emailError != null,
                    supportingText = {
                        Text(emailError ?: "", color = BrandColors.Error, style = MaterialTheme.typography.labelSmall)
                    }
                )

                Spacer(modifier = Modifier.height(8.dp))

                // Password
                OutlinedTextField(
                    value = password,
                    onValueChange = {
                        password = it
                        passwordError = null
                    },
                    modifier = Modifier.fillMaxWidth(),
                    placeholder = { Text("Password") },
                    leadingIcon = {
                        Icon(imageVector = Icons.Default.Lock, contentDescription = null)
                    },
                    visualTransformation = PasswordVisualTransformation(),
                    singleLine = true,
                    shape = RoundedCornerShape(16.dp),
                    colors = authTextFieldColors(),
                    isError = passwordError != null,
                    supportingText = {
                        Text(passwordError ?: "", color = BrandColors.Error, style = MaterialTheme.typography.labelSmall)
                    }
                )

                // Forgot password link (login mode only)
                if (isLogin) {
                    TextButton(
                        onClick = {
                            isForgotPassword = true
                            emailError = null
                            authViewModel.resetState()
                        },
                        modifier = Modifier.align(Alignment.End)
                    ) {
                        Text(
                            text = "Forgot password?",
                            color = BrandColors.Primary,
                            style = MaterialTheme.typography.labelMedium
                        )
                    }
                    Spacer(modifier = Modifier.height(8.dp))
                } else {
                    Spacer(modifier = Modifier.height(24.dp))
                }

                // Login / Signup button
                Button(
                    onClick = {
                        val trimmedEmail = email.trim()
                        var valid = true

                        if (trimmedEmail.isBlank()) {
                            emailError = "Email is required."
                            valid = false
                        } else if (!isValidEmail(trimmedEmail)) {
                            emailError = "Please enter a valid email address."
                            valid = false
                        }

                        if (password.isBlank()) {
                            passwordError = "Password is required."
                            valid = false
                        } else if (!isLogin && password.length < 6) {
                            passwordError = "Password must be at least 6 characters."
                            valid = false
                        }

                        if (!valid) return@Button

                        if (isLogin) {
                            authViewModel.login(trimmedEmail, password)
                        } else {
                            authViewModel.signup(trimmedEmail, password)
                        }
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.dp),
                    shape = RoundedCornerShape(16.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = BrandColors.Primary
                    )
                ) {
                    when (authState) {
                        is AuthState.Loading -> CircularProgressIndicator(
                            color = Color.Black,
                            strokeWidth = 2.dp,
                            modifier = Modifier.size(24.dp)
                        )
                        else -> Text(
                            text = if (isLogin) "Login" else "Create Account",
                            color = Color.Black,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }

                // Legal consent (sign-up only)
                if (!isLogin) {
                    Spacer(modifier = Modifier.height(16.dp))
                    val uriHandler = LocalUriHandler.current
                    val legalText = buildAnnotatedString {
                        withStyle(SpanStyle(color = Color.Gray, fontSize = 12.sp)) { append("By signing up you agree to our ") }
                        pushStringAnnotation("TERMS", "https://bingeamigo.com/terms-and-conditions.html")
                        withStyle(SpanStyle(color = BrandColors.Primary, fontSize = 12.sp)) { append("Terms & Conditions") }
                        pop()
                        withStyle(SpanStyle(color = Color.Gray, fontSize = 12.sp)) { append(" and ") }
                        pushStringAnnotation("PRIVACY", "https://bingeamigo.com/privacy-policy.html")
                        withStyle(SpanStyle(color = BrandColors.Primary, fontSize = 12.sp)) { append("Privacy Policy") }
                        pop()
                        withStyle(SpanStyle(color = Color.Gray, fontSize = 12.sp)) { append(".") }
                    }
                    ClickableText(
                        text = legalText,
                        style = MaterialTheme.typography.bodySmall.copy(textAlign = TextAlign.Center),
                        modifier = Modifier.fillMaxWidth(),
                        onClick = { offset ->
                            legalText.getStringAnnotations("TERMS", offset, offset).firstOrNull()
                                ?.let { uriHandler.openUri(it.item) }
                            legalText.getStringAnnotations("PRIVACY", offset, offset).firstOrNull()
                                ?.let { uriHandler.openUri(it.item) }
                        }
                    )
                }

                Spacer(modifier = Modifier.height(20.dp))

                // Error state
                if (authState is AuthState.Error) {
                    Text(
                        text = authState.message,
                        color = BrandColors.Error,
                        style = MaterialTheme.typography.bodySmall,
                        textAlign = TextAlign.Center
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                }

                // Switch login/signup
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = if (isLogin) "Don't have an account?" else "Already have an account?",
                        color = Color.Gray
                    )
                    TextButton(
                        onClick = {
                            isLogin = !isLogin
                            emailError = null
                            passwordError = null
                            authViewModel.resetState()
                        }
                    ) {
                        Text(
                            text = if (isLogin) "Sign Up" else "Login",
                            color = BrandColors.Primary,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        }
        }
        }
    }
}

private fun isValidEmail(email: String): Boolean =
    Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+\$").matches(email)
